import React, { useEffect, useState } from "react";
import { DollarSign, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import InfoTip from "@/components/ui/InfoTip";

interface FeatureSummary {
  feature: string;
  totalCalls: number;
  totalCost: number;
  avgDurationMs: number;
  successRate: number;
  evalPassRate: number | null;
}

interface UsageSummary {
  overall: FeatureSummary;
  byFeature: FeatureSummary[];
}

interface UsageLog {
  id: string;
  createdAt: string;
  feature: string;
  refId: string | null;
  model: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
  durationMs: number;
  success: boolean;
  errorMessage: string | null;
  evalScore: number | null;
  evalId: string | null;
}

interface PaginatedLogs {
  items: UsageLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const RANGE_OPTIONS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
];

/** Plain-language description of each LLM-backed feature, keyed by the name it logs under. */
const FEATURE_INFO: Record<string, string> = {
  "ai-counselor":
    "The AI Counselor chat. Each call is the AI generating a reply to a student in the chat.",
  "ai-counselor-summarization":
    "Condenses a long AI Counselor conversation into a short summary, so the AI and the counseling team can pick up the context without rereading every message.",
  "ai-counselor-stage-classifier":
    "Reads a WhatsApp counseling conversation and labels which of four journey stages the student is in. Runs in the daily conversation-analysis job.",
  "ai-counselor-guideline-conflict":
    "Checks a new or edited AI guideline against the existing ones and flags any that contradict each other, before they confuse the AI Counselor.",
  "mba-narrative":
    "Writes the plain-English explanation in the MBA readiness report: why each college fits the student, based on their scores.",
  "campaign-vetter":
    "Checks one WhatsApp template against one student before sending: is it a good fit, and did the AI fill in every personalised blank?",
  "reengagement-selector":
    "Looks at all approved WhatsApp templates at once and picks the one that best suits a student we want to win back.",
  "template-suggestion":
    "Drafts new WhatsApp templates for gaps in the library (e.g. nothing fits a topic, or a template performs badly). Drafts only; a person reviews before anything is submitted.",
  "session-summary-counseling-session":
    "Writes the admin-review summary of a finished counseling session (student with a human counselor): what was discussed and the key points, so an admin doesn't have to read the whole conversation.",
  "session-summary-chat-session":
    "Writes the admin-review summary of a finished chat session between a student and a counselor, so an admin doesn't have to read the whole conversation.",
  "college-verification":
    "Weekly job that re-checks stale college data (rank, acceptance rate, placement packages) against a live web search and updates it.",
};

const SUMMARY_INFO = {
  totalCost:
    "Estimated spend on AI calls in the selected period. It is calculated from token counts and a built-in price list per model, so it is an estimate, not the actual bill.",
  totalCalls: "How many times an AI model was called in the selected period, across all features.",
  latency:
    "Average time one AI call took from request to response, in seconds. Lower is faster for the student.",
  evalPassRate:
    "Average score from the automated quality checks (evals) that grade AI output, e.g. whether it invented numbers or left a blank unfilled. 100% means every check was passed. A dash means no quality check runs yet for that feature.",
};

const COLUMN_INFO = {
  feature:
    "The part of the product that made the AI call. The (i) next to each feature name describes what it does.",
  calls: "Number of AI calls this feature made in the selected period.",
  cost: "Estimated spend, calculated from token counts and a built-in price list. Not an exact bill.",
  latency: "Average time one call took from request to response, in seconds.",
  success:
    "Share of calls that finished without an error. A low number usually points to a rate limit, a bad prompt, or a malformed response, not necessarily a wrong answer.",
  evalPass:
    "Average score from the automated quality checks (evals) on this feature's output. 100% means all checks passed. A dash means no quality check runs for this feature yet.",
  time: "When the AI call was made.",
  model: "Which AI model handled the call.",
  tokens:
    "Tokens are the chunks of text the AI reads and writes (roughly three-quarters of a word each). This is input plus output, and it drives the cost.",
  eval:
    "Quality-check score for this one call, from 0 to 100%. A dash means no quality check ran on this call.",
  status:
    "Green tick: the call finished. Red cross: it failed. Hovering the red cross shows the error message.",
};

function formatCost(usd: number | string): string {
  const value = Number(usd);
  if (!value) return "$0.00";
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

const AiCosts: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [logs, setLogs] = useState<PaginatedLogs | null>(null);
  const [rangeDays, setRangeDays] = useState(30);
  const [featureFilter, setFeatureFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await api.get<UsageSummary>("/llm-usage/admin/summary", {
          from: isoDaysAgo(rangeDays),
        });
        setSummary(data);
      } catch {
        setSummary(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [rangeDays]);

  useEffect(() => {
    (async () => {
      setLogsLoading(true);
      try {
        const params: Record<string, string> = {
          from: isoDaysAgo(rangeDays),
          page: String(page),
          limit: "20",
        };
        if (featureFilter) params.feature = featureFilter;
        const data = await api.get<PaginatedLogs>("/llm-usage/admin/logs", params);
        setLogs(data);
      } catch {
        setLogs(null);
      } finally {
        setLogsLoading(false);
      }
    })();
  }, [rangeDays, featureFilter, page]);

  const overall = summary?.overall;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-primary shrink-0" />
            AI Costs
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Token spend and call outcomes across every LLM-backed feature.
          </p>
        </div>
        <div className="flex gap-1.5">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              type="button"
              onClick={() => {
                setRangeDays(opt.days);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                rangeDays === opt.days
                  ? "bg-primary text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="md" />
        </div>
      ) : !overall ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-sm text-gray-500">
          Couldn't load usage data.
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard
              label="Total Cost"
              value={formatCost(overall.totalCost)}
              info={SUMMARY_INFO.totalCost}
            />
            <SummaryCard
              label="Total Calls"
              value={overall.totalCalls.toLocaleString()}
              info={SUMMARY_INFO.totalCalls}
            />
            <SummaryCard
              label="Avg Latency"
              value={`${(overall.avgDurationMs / 1000).toFixed(1)}s`}
              info={SUMMARY_INFO.latency}
            />
            <SummaryCard
              label="Eval Pass Rate"
              value={overall.evalPassRate !== null ? `${Math.round(overall.evalPassRate * 100)}%` : "—"}
              info={SUMMARY_INFO.evalPassRate}
            />
          </div>

          {/* Per-feature breakdown */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">By Feature</h2>
            </div>
            {summary.byFeature.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">No usage recorded in this range yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                      <Th first label="Feature" info={COLUMN_INFO.feature} />
                      <Th label="Calls" info={COLUMN_INFO.calls} />
                      <Th label="Cost" info={COLUMN_INFO.cost} />
                      <Th label="Avg Latency" info={COLUMN_INFO.latency} />
                      <Th label="Success" info={COLUMN_INFO.success} />
                      <Th label="Eval Pass" info={COLUMN_INFO.evalPass} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {summary.byFeature.map((f) => (
                      <tr key={f.feature}>
                        <td className="px-4 sm:px-6 py-2.5 font-medium text-gray-900">
                          <FeatureName name={f.feature} />
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">{f.totalCalls}</td>
                        <td className="px-4 py-2.5 text-gray-600">{formatCost(f.totalCost)}</td>
                        <td className="px-4 py-2.5 text-gray-600">{(f.avgDurationMs / 1000).toFixed(1)}s</td>
                        <td className="px-4 py-2.5 text-gray-600">{Math.round(f.successRate * 100)}%</td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {f.evalPassRate !== null ? `${Math.round(f.evalPassRate * 100)}%` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent calls */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 border-b border-gray-100 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Recent Calls</h2>
              <select
                value={featureFilter}
                onChange={(e) => {
                  setFeatureFilter(e.target.value);
                  setPage(1);
                }}
                className="text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white"
              >
                <option value="">All features</option>
                {summary.byFeature.map((f) => (
                  <option key={f.feature} value={f.feature}>
                    {f.feature}
                  </option>
                ))}
              </select>
            </div>
            {logsLoading ? (
              <div className="flex justify-center py-10">
                <LoadingSpinner size="sm" />
              </div>
            ) : !logs || logs.items.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">No calls in this range.</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                        <Th first label="Time" info={COLUMN_INFO.time} />
                        <Th label="Feature" info={COLUMN_INFO.feature} />
                        <Th label="Model" info={COLUMN_INFO.model} />
                        <Th label="Tokens" info={COLUMN_INFO.tokens} />
                        <Th label="Cost" info={COLUMN_INFO.cost} />
                        <Th label="Latency" info={COLUMN_INFO.latency} />
                        <Th label="Eval" info={COLUMN_INFO.eval} />
                        <Th label="Status" info={COLUMN_INFO.status} />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {logs.items.map((log) => (
                        <tr key={log.id}>
                          <td className="px-4 sm:px-6 py-2.5 text-gray-500 whitespace-nowrap">
                            {new Date(log.createdAt).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                          <td className="px-4 py-2.5 text-gray-900 font-medium">
                            <FeatureName name={log.feature} />
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">{log.model}</td>
                          <td className="px-4 py-2.5 text-gray-600">{log.totalTokens.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-gray-600">{formatCost(log.costUsd)}</td>
                          <td className="px-4 py-2.5 text-gray-600">{(log.durationMs / 1000).toFixed(1)}s</td>
                          <td className="px-4 py-2.5 text-gray-600">
                            {log.evalScore !== null ? Math.round(log.evalScore * 100) + "%" : "—"}
                          </td>
                          <td className="px-4 py-2.5">
                            {log.success ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            ) : (
                              <span title={log.errorMessage ?? undefined}>
                                <XCircle className="h-4 w-4 text-red-500" />
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 sm:px-6 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                  <span>
                    Page {logs.page} of {Math.max(logs.totalPages, 1)} — {logs.total} total
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="px-2.5 py-1 rounded-md border border-gray-200 disabled:opacity-40"
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      disabled={page >= logs.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      className="px-2.5 py-1 rounded-md border border-gray-200 disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const Th: React.FC<{ label: string; info: string; first?: boolean }> = ({ label, info, first }) => (
  <th className={first ? "px-4 sm:px-6 py-2.5" : "px-4 py-2.5"}>
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      {label}
      <InfoTip title={label} text={info} />
    </span>
  </th>
);

const FeatureName: React.FC<{ name: string }> = ({ name }) => (
  <span className="inline-flex items-center gap-1.5">
    {name}
    {FEATURE_INFO[name] && <InfoTip title={name} text={FEATURE_INFO[name]} />}
  </span>
);

const SummaryCard: React.FC<{ label: string; value: string; info: string }> = ({
  label,
  value,
  info,
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
      {label}
      <InfoTip title={label} text={info} />
    </p>
    <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
  </div>
);

export default AiCosts;
